const en = {
  meta: {
    home: {
      title: "Centauros — Get paid without chasing clients",
      description:
        "Set payment policies, automate reminders, generate invoices and get paid without chasing anyone. All from your phone.",
    },
    privacy: { title: "Privacy Policy — Centauros", description: "How Centauros protects and handles your data." },
    terms: { title: "Terms of Service — Centauros", description: "Terms and conditions for using Centauros." },
    support: { title: "Support — Centauros", description: "Help and answers about Centauros." },
    contact: { title: "Contact — Centauros", description: "Talk to the Centauros team." },
  },
  nav: { features: "Features", how: "How it works", faq: "FAQ", contact: "Contact", download: "Download", dashboard: "Dashboard" },
  hero: {"line1": "Getting paid shouldn't", "line2": "be another job.", "subtitle": "Set the rules from the start, automate follow-ups and stop chasing your clients to get paid.", "functional": "Centauros lets you define payment policies, automate reminders, generate invoices and keep every payment under control.", "ctaPrimary": "Download Centauros", "ctaSecondary": "See how it works", "note": "No hidden fees · Secure payments with Stripe", "availability": "Available for iPhone and Android"},
  marquee: ["Built for equine professionals", "Secure", "Fast", "Reliable", "Get paid without chasing"],
  problem: {"label": "The problem", "title": "Getting paid is the problem. Doing great work isn't enough if getting paid becomes another job.", "intro": "You get paid late, chase your clients and front time, materials and money. These are the real costs.", "chapters": [{"n": "01", "title": "Payments that arrive late", "text": "You end up financing your client with your own time and money."}, {"n": "02", "title": "Chasing clients", "text": "Awkward texts, calls and emails that should be avoidable."}, {"n": "03", "title": "Unclear rules", "text": "When terms aren't set upfront, asking for money later creates conflict."}, {"n": "04", "title": "Unpaid and unpredictable", "text": "Not knowing what you'll collect, when, or what's overdue."}, {"n": "05", "title": "Opportunity cost", "text": "Every hour chasing payments is an hour not spent working or winning clients."}]},
  solution: {
    label: "The solution",
    title: "Centauros automates collection, end to end.",
    subtitle: "Set it once. Let it work for you.",
    items: ["Payment policies", "Reminders", "Invoices", "Payments", "Tracking", "Reconciliation"],
    footer: "All from your phone.",
  },
  how: {
    label: "How it works",
    title: "Four steps. Zero friction.",
    steps: [
      { title: "Create client", text: "Add your client in seconds with their details." },
      { title: "Choose policy", text: "Define when and how payment happens automatically." },
      { title: "Send", text: "Centauros issues and sends the invoice for you." },
      { title: "Get paid", text: "Payment arrives on its own. No manual reminders." },
    ],
  },
  screens: {
    label: "Screens",
    title: "Designed with obsessive attention to detail.",
    subtitle: "Every screen, built to make getting paid invisible.",
    items: [
      { title: "Collections", text: "Everything you're owed, at a glance." },
      { title: "Policies", text: "Clear, reusable payment rules." },
      { title: "Invoices", text: "Generate and send in one tap." },
      { title: "Payments", text: "Secure, powered by Stripe, in real time." },
    ],
  },
  features: {
    label: "Features",
    title: "Everything you need to get paid better.",
    cards: [
      { title: "Full control", text: "A clear view of every payment and client." },
      { title: "Automation", text: "Repetitive tasks run themselves." },
      { title: "Invoicing", text: "Professional invoices generated instantly." },
      { title: "Collections", text: "Automatic payments based on your policies." },
      { title: "Team", text: "Invite your team and work together." },
      { title: "Security", text: "Secure infrastructure and protected data." },
      { title: "Payments", text: "Get paid by card securely via Stripe." },
      { title: "History", text: "Every movement logged and reconciled." },
    ],
  },
  audience: {
    label: "Who it's for",
    title: "Made for people who live from their work.",
    subtitle: "If your time matters, Centauros is for you.",
    roles: ["Veterinarians", "Farriers", "Trainers", "Transporters", "Equine professionals"],
    note: "And any professional who wants to get paid better.",
  },
  security: {
    label: "Security",
    title: "Your money and data, protected.",
    subtitle: "Built on trusted infrastructure.",
    points: [
      { title: "Payments with Stripe", text: "We process payments with the industry-leading standard." },
      { title: "Secure infrastructure", text: "Encryption in transit and at rest." },
      { title: "Privacy", text: "Your data is yours. We never sell it." },
      { title: "Data protection", text: "Compliance and best practices by design." },
      { title: "No stored cards", text: "We never store card details." },
    ],
  },
  faq: {
    label: "Frequently asked questions",
    title: "Everything you want to know.",
    items: [
      { q: "What is Centauros?", a: "An app to define payment policies, automate reminders, generate invoices and get paid without chasing anyone." },
      { q: "Do I need technical skills?", a: "No. It's set up in minutes and works from your phone." },
      { q: "How are payments processed?", a: "Payments are processed securely through Stripe." },
      { q: "Do you store my card details?", a: "Never. We don't store card details at any point." },
      { q: "Can I automate reminders?", a: "Yes. Define the policy once and Centauros handles the rest." },
      { q: "Can I generate invoices?", a: "Yes, professional invoices instantly, ready to send." },
      { q: "Does it work for my profession?", a: "It's built for equine professionals and anyone who wants to get paid better." },
      { q: "Can I work with my team?", a: "Yes, you can invite your team and collaborate." },
      { q: "Is it available in multiple languages?", a: "Yes, Centauros is available in 12 languages." },
      { q: "Where can I download it?", a: "Coming soon to the App Store for iPhone." },
    ],
  },
  finalCta: { title: "Start today.", subtitle: "Download Centauros and stop chasing clients.", badge: "Download on the App Store" },
  footer: {
    statement1: "Getting paid stops being",
    statement2: "another job.",
    download: "Download Centauros",
    secure: "Secure payments",
    tagline: "Get paid without chasing clients.",
    colProduct: "Product",
    colCompany: "Company",
    colLegal: "Legal",
    links: { home: "Home", features: "Features", faq: "FAQ", contact: "Contact", privacy: "Privacy", terms: "Terms", support: "Support", download: "Download" },
    contactLabel: "Contact",
    rights: "© Centauros. All rights reserved.",
  },
  contact: {
    title: "Let's talk.",
    subtitle: "Have a question or want to know more? Write to us.",
    fields: { name: "Name", email: "Email", subject: "Subject", message: "Message" },
    placeholders: { name: "Your name", email: "you@email.com", subject: "How can we help?", message: "Tell us..." },
    submit: "Send message",
    sending: "Sending...",
    success: "Message sent. We'll get back to you soon.",
    error: "Couldn't send. Please try again.",
    directLabel: "Or write to us directly at",
  },
  legal: {
    privacy: {
      title: "Privacy Policy",
      updated: "Last updated: June 2026",
      body: [
        { h: "Introduction", p: "At Centauros we take your privacy seriously. This policy explains what data we process and why." },
        { h: "Data we process", p: "We process the data needed to provide the service: your account, the clients you manage and the payments you make." },
        { h: "Payments", p: "Payments are processed through Stripe. We never store your card details or those of your clients." },
        { h: "Use of data", p: "We use your data only to provide and improve the service. We never sell your information to third parties." },
        { h: "Your rights", p: "You can access, rectify or delete your data at any time by writing to support@centauros.app." },
      ],
    },
    terms: {
      title: "Terms of Service",
      updated: "Last updated: June 2026",
      body: [
        { h: "Acceptance", p: "By using Centauros you accept these terms. If you disagree, don't use the service." },
        { h: "Use of the service", p: "You agree to use Centauros lawfully and to keep your account secure." },
        { h: "Payments", p: "Payments are processed via Stripe under its terms. You're responsible for your client relationships." },
        { h: "Liability", p: "Centauros is provided \"as is\". We work to ensure its ongoing availability and security." },
        { h: "Changes", p: "We may update these terms. We'll notify relevant changes through the app." },
      ],
    },
    support: {
      title: "Support",
      updated: "We're here to help.",
      intro: "Find quick answers or contact us directly. We reply as soon as possible.",
      faqs: [
        { q: "How do I start?", a: "Download the app, create your account and add your first client in minutes." },
        { q: "How do I automate collection?", a: "Create a payment policy and assign it to your clients. The rest is automatic." },
        { q: "I have a payment issue?", a: "Write to support@centauros.app and we'll look into it with you." },
        { q: "Can I export my data?", a: "Yes, you can request a data export at any time." },
      ],
      contactLine: "Need more help? Write to us at",
    },
  },
  common: { languageLabel: "Language", backHome: "Back to home", loading: "Loading..." },
  philosophy: {"label": "The philosophy", "title": "Payment rules are set", "titleGreen": "before you start.", "text": "Sometimes it's better to turn down a client than to take one who forces you to front time, money and resources without knowing when you'll be paid. Centauros lets you agree upfront how, when and how much will be paid.", "points": ["Define payment policies", "Prevent problems", "Secure commitments", "Automate follow-up", "Keep financial control", "Reduce friction with clients"]},
  showcase: {"label": 'The product', "slides": [{"id": "control", "title": "Control everything", "green": "you need to collect", "text": "A clear view of every payment and your business, always at hand."}, {"id": "policies", "title": "You decide", "green": "how and when they pay you", "text": "Upfront, deposit + balance, on completion or recurring. Pick the policy that fits."}, {"id": "clients", "title": "Every client,", "green": "all the info you need", "text": "Details, payments, subscriptions and history in one place."}, {"id": "payments", "title": "Every payment,", "green": "under control", "text": "Real-time status, automatic reminders and instant invoicing."}, {"id": "impagos", "title": "Catch unpaid invoices", "green": "before they grow", "text": "Centauros follows up, chases and helps you recover what's overdue."}, {"id": "facturacion", "title": "Payments and invoices", "green": "always reconciled", "text": "Issue, review and reconcile all your invoices from one place."}, {"id": "equipo", "title": "Your team,", "green": "all under control", "text": "Invite your team, assign roles and manage collections together."}]},
  badges: {"appStoreTop": "Download on the", "appStoreLabel": "App Store", "playTop": "Get it on", "playLabel": "Google Play"},
  partners: {"label": "Powered by leading payment partners"},
  dashboard: {
    loginTitle: "Centauros Dashboard",
    loginSubtitle: "Private admin access.",
    email: "Email",
    password: "Password",
    signIn: "Sign in",
    signingIn: "Signing in...",
    invalid: "Invalid credentials.",
    logout: "Log out",
    nav: { overview: "Overview", users: "Users", workspaces: "Workspaces", activity: "Activity", incidents: "Incidents" },
    metrics: { users: "Total users", active: "Active users", workspaces: "Workspaces", incidents: "Open incidents", collected: "Total collected" },
    tableUsers: { name: "Name", email: "Email", profession: "Profession", status: "Status" },
    tableWorkspaces: { name: "Workspace", members: "Members", plan: "Plan", collected: "Collected" },
    tableIncidents: { title: "Title", workspace: "Workspace", severity: "Severity", status: "Status" },
    recentActivity: "Recent activity",
  },
  policies: {
    label: "Payment policies",
    title: "You decide how and when you get paid.",
    subtitle: "Set the policy that fits each client and Centauros applies it automatically.",
    methods: [
      { name: "Upfront payment", desc: "Charge the full amount before you start. Zero risk." },
      { name: "Deposit + balance", desc: "A deposit now and the rest when the job is done." },
      { name: "Payment on completion", desc: "The client pays automatically when you finish." },
      { name: "Recurring payment", desc: "Subscriptions and fees that charge themselves each period." },
      { name: "Later extras", desc: "Add extra charges after the service, with no friction." },
      { name: "Milestone payments", desc: "Split the charge into phases and get paid as you progress." },
    ],
    ui: { upfront: "Upfront", paid: "Paid", pending: "Pending", total: "Total", deposit: "Deposit", balance: "Balance", completed: "Completed", dueOnFinish: "On completion", monthly: "Monthly", nextCharge: "Next charge", extra: "Extra", milestone: "Milestone" },
  },
  redesign: { comingSoon: "Coming soon" },
  appui: { hello: "Hi", toCollect: "To collect", collected: "Collected", overdue: "Overdue", activity: "Activity", pending: "Pending", paid: "Paid", advance: "Upfront", extra: "Extra", payNow: "Pay now" },
  faqx: [
    { q: "What is Centauros?", a: "Centauros is a platform to define payment policies, automate payments, generate invoices and reduce late payments without chasing your clients. You set your rules once and the app charges, reminds and reconciles for you." },
    { q: "Who is it for?", a: "For any professional or business that charges for their services: trainers, vets, lawyers, consultants, agencies, clinics, gyms or service companies. If you bill on a recurring or per-project basis, Centauros adapts to how you work." },
    { q: "Do my clients need to install the app?", a: "No. Your clients receive payment links and messages directly, with nothing to download. Only you, the professional, use the Centauros app." },
    { q: "Which payment methods can I accept?", a: "Thanks to Stripe you can accept credit and debit cards, Apple Pay, Google Pay and the other methods available in your country. The exact methods depend on Stripe's coverage in each region." },
    { q: "Can I decide how and when I get paid?", a: "Yes. Set upfront payment, deposit + balance, payment on completion, recurring payments, later extras or milestone payments. You choose the policy that best fits each client or service." },
    { q: "Does Centauros generate invoices automatically?", a: "Yes. It can create drafts, issue invoices automatically after payment or follow the invoicing policy you configure. They are all recorded and ready to send." },
    { q: "What happens if a client doesn't pay?", a: "Centauros follows up for you: it sends automatic reminders, tracks due dates and flags late payments, helping you recover what's outstanding without awkward conversations." },
    { q: "Is Centauros secure?", a: "Yes. Payments are processed entirely through Stripe, data travels encrypted and we never store card details. The platform follows security best practices throughout." },
    { q: "Which countries is it available in?", a: "Centauros is built to operate internationally. Available payment methods depend on the countries supported by Stripe, which covers most markets in Europe, the Americas and other regions." },
    { q: "How do I get started?", a: "Download the app, create your account and set up your first payment policy in minutes. From there you can start collecting and automating your payments." },
  ],
  legalx: {
    eyebrow: "Legal", contents: "Contents", questions: "Questions about this page? Get in touch.",
    navPrivacy: "Privacy", navTerms: "Terms", navCookies: "Cookies", navSupport: "Support",
    cookiesMetaTitle: "Cookie Policy — Centauros", cookiesMetaDesc: "How Centauros uses cookies and similar technologies.",
    privacy: {
      title: "Privacy Policy", updated: "Last updated: June 2026", intro: "How Centauros collects, uses and protects your personal data.",
      body: [
        { h: "Introduction", p: "At Centauros, privacy is a design principle, not an add-on. This policy explains what data we process, for what purpose, for how long and what rights you have over it." },
        { h: "Data controller", p: "The data controller is Centauros. For any question about this policy or your data, write to us at support@centauros.app." },
        { h: "Data we process", p: "We process your account data (name, email and credentials), the data of the clients you manage, your payment and invoice information, and technical usage data needed to run the service." },
        { h: "Purpose of processing", p: "We use your data to provide the service, process payments, generate invoices, send reminders, support you and improve the platform. We do not use it for purposes unrelated to the service." },
        { h: "Payments and Stripe", p: "Payments are processed through Stripe. We never store full card details: they are handled directly by Stripe under the PCI-DSS standard." },
        { h: "Data retention", p: "We keep your data while your account is active and for as long as needed to meet legal, accounting and tax obligations. After that we delete or anonymise it." },
        { h: "Sharing information", p: "We do not sell your information. We only share data with providers needed to run the service (such as Stripe or hosting infrastructure), always under data-protection agreements." },
        { h: "Your rights", p: "You can access, rectify, delete, restrict or object to the processing of your data, and request its portability, by writing to support@centauros.app. We will handle your request as soon as possible." },
        { h: "Security and changes", p: "We apply encryption in transit and at rest and access controls to protect your information. We may update this policy and will always state the last-updated date." },
      ],
    },
    terms: {
      title: "Terms of Service", updated: "Last updated: June 2026", intro: "The terms that govern your use of Centauros.",
      body: [
        { h: "Acceptance of terms", p: "By creating an account or using Centauros you accept these Terms of Service. If you do not agree with them, you must not use the platform." },
        { h: "Service description", p: "Centauros is a tool to define payment policies, automate payments and reminders, generate invoices and manage late payments. We act as a technology platform; we are not a party to the commercial relationship between you and your clients." },
        { h: "Your account", p: "You are responsible for the accuracy of your account data and for keeping your credentials confidential. Any activity from your account is considered to be performed by you." },
        { h: "Acceptable use", p: "You agree to use Centauros lawfully, without infringing third-party rights or using the platform for fraudulent or unauthorised activities." },
        { h: "Payments and Stripe", p: "Payments are processed through Stripe and subject to its terms. You are responsible for the relationship with your clients, the legality of your charges and your tax obligations." },
        { h: "Invoicing", p: "Centauros helps generate invoices, but responsibility for their content, accuracy and legal compliance lies with the professional who issues them." },
        { h: "Intellectual property", p: "The platform, its brand, design and software are owned by Centauros. These terms grant you no rights over them beyond using the service." },
        { h: "Availability and liability", p: "Centauros is provided 'as is'. We work to ensure its availability and security but do not guarantee uninterrupted operation, nor do we assume losses arising from misuse." },
        { h: "Cancellation and changes", p: "You can close your account at any time; we may suspend accounts that breach these terms. We may update them and will notify relevant changes with the date." },
      ],
    },
    cookies: {
      title: "Cookie Policy", updated: "Last updated: June 2026", intro: "How we use cookies and similar technologies on centauros.app.",
      body: [
        { h: "What cookies are", p: "Cookies are small files stored on your device when you visit a website. They help remember preferences and understand how the site is used." },
        { h: "How we use cookies", p: "We use cookies for the basic operation of the site, to remember your language and to measure usage in aggregate to improve the experience." },
        { h: "Essential cookies", p: "These are required for the site to work properly, such as remembering your language or keeping your session. They cannot be disabled." },
        { h: "Analytics cookies", p: "They help us understand site usage anonymously and in aggregate. Where applicable, they are only enabled with your consent." },
        { h: "Managing cookies", p: "You can configure or delete cookies from your browser settings. Disabling some may affect how the site works." },
        { h: "Changes to this policy", p: "We may update this cookie policy. We will post any changes on this page, stating the last-updated date." },
      ],
    },
  },
  product2: {
    p1title: "Stop chasing. Start collecting.",
    p1sub: "Manual follow-up makes you lose money. With Centauros you set the policy and get paid on autopilot, no awkward conversations.",
    p1cta: "Download Centauros",
    p1link: "Learn how it works",
    p1cardTitle: "For every €1,000 invoiced",
    p1cardCol: "You actually collect",
    p1rows: [
      { name: "Centauros", amount: "€1,000" },
      { name: "Manual reminders", amount: "€910", delta: "−€90" },
      { name: "No follow-up", amount: "€820", delta: "−€180" },
    ],
    p2title: "Get paid for your work, friction-free.",
    p2sub: "Payment links, policies and automatic invoices. You set the rules once and Centauros does the rest.",
    p2cta: "Learn how it works",
    p2policy: "Policy · Deposit + balance",
    p2sendLabel: "The client pays",
    p2client: "Client",
    p2info: "Recurring charge? Set it up once and it collects itself every period.",
    p2recipLabel: "You receive",
    p2you: "You",
    p2arrives: "Charged",
    p2arrivesVal: "Today · automatic",
    p2fees: "Fees",
    p2feesVal: "Included",
    p3title: "Do so much more with Centauros.",
    p3sub: "It's not only collecting: manage clients, invoices, late payments and your team from one place.",
    p3cta: "Create account",
    p3cardLabel: "To collect",
    p3feat: "Get paid instantly",
    p3featDesc: "Payment links and automatic collection powered by Stripe's infrastructure.",
    p3check: "Multiple payment methods",
    p3cta2: "Explore collections",
    p4title: "Recover the late payments you'd written off.",
    p4sub: "Centauros automatically chases overdue payments with reminders and retries, until you get paid.",
    p4cta: "Learn how it works",
    p4cardLabel: "Recovered defaults",
    p4foot: "Professionals recover on average 38% more in defaults within the first 6 months.",
  },
  pricing: {
    label: "Pricing",
    title: "Clear pricing. No surprises.",
    subtitle: "You only pay when you get paid. Stripe charges its fee for processing the payment and Centauros adds 1% for the automation, collections management and the whole platform.",
    stripeTitle: "Stripe fee",
    stripeDesc: "Stripe securely processes the payment. Its fee varies by country and payment method; the example shown is for European cards.",
    centaurosTitle: "Centauros fee",
    centaurosDesc: "1% of the amount for automation, collections follow-up and the whole platform. No hidden monthly fees.",
    calcTitle: "See what you receive",
    invoiceAmount: "Invoice amount",
    stripeFee: "Stripe fee",
    centaurosFee: "Centauros fee (1%)",
    received: "The professional receives",
    note: "Stripe's real fee may vary by country and payment method. The amounts shown are an indicative example.",
  },
  cookie: {
    title: "Your privacy, your choice",
    intro: "We use cookies to make Centauros work properly, remember your preferences and anonymously understand how the site is used. You choose what to enable, and you can change your mind whenever you like.",
    required: "Always on",
    acceptAll: "Accept all",
    rejectAll: "Reject all",
    save: "Save preferences",
    reopen: "Cookie preferences",
    footnote: "You can change your consent at any time from the footer.",
    policyLink: "Cookie policy",
    categories: {
      necessary: { name: "Necessary", desc: "Essential for the site to work and stay secure. These can't be turned off." },
      preferences: { name: "Preferences", desc: "Remember your settings, like language, for a smoother experience." },
      analytics: { name: "Analytics", desc: "Help us anonymously understand how the site is used so we can improve it." },
      marketing: { name: "Marketing", desc: "Allow us to show more relevant content and ads on and off the site." },
    },
  },
};
export default en;
